import React from "react";
import { Box, Tab, type SxProps, type Theme } from "@mui/material";
import { useTranslation } from "next-i18next";
import { IWikifolioDetailDict } from "src/api/server/wf-detail.types";
import { Typography } from "src/components-mui/base/typography/typography";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { ErrorBoundary } from "src/components-mui/common/error-boundary/error-boundary";
import { TabPanel } from "src/components-mui/tabs/tab-panel";
import { TabsContext } from "src/components-mui/tabs/tabs-context";
import { TabsList } from "src/components-mui/tabs/tabs-list";

const TABS = {
    SECTOR: 0,
    COUNTRY: 1,
} as const;

const TAB_PANEL_STYLES: SxProps<Theme> = {
    display: "flex",
    flexDirection: {
        xs: "column",
        sm: "row",
    },
    justifyContent: "center",
    width: "100%",
    gap: [1, 5],
};

interface IPortfolioDistributionShellProps {
    dict: IWikifolioDetailDict["portfolioDistribution"];
    Chip: React.ReactElement;
    PieChart: React.ReactElement;
    MapChart: React.ReactElement;
}

/**
 * Portfolio Distribution Shell - Layout component for portfolio distribution charts
 * Provides the structure with header, tabs, and content areas for charts and data visualization
 */
export const PortfolioDistributionShell = ({ dict, Chip, PieChart, MapChart }: IPortfolioDistributionShellProps) => {
    const { t } = useTranslation("wf-detail");

    return (
        <>
            <Box
                component="header"
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexDirection: {
                        xs: "column",
                        sm: "row",
                    },
                    gap: {
                        xs: 1,
                        sm: 0,
                    },
                }}
            >
                <Box
                    sx={{
                        gap: 2,
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                    }}
                >
                    <WfHeading component="h2" fontSize="2xl">
                        {dict.title}
                    </WfHeading>
                    <Box
                        sx={{
                            gap: [1, 2, 5, 20],
                            display: "flex",
                            flexDirection: ["column", "row"],
                            alignItems: ["flex-start", "center"],
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "md",
                            }}
                        >
                            {dict.description}
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                alignSelf: ["flex-end", "center"],
                                gap: 1,
                                flexShrink: 0,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: "bold",
                                }}
                            >
                                {t("portfolio-distribution.stock-percentage")}
                            </Typography>
                            {Chip}
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ mt: 3, display: "flex", flexFlow: "column", alignItems: "center" }}>
                <TabsContext initialValue={TABS.SECTOR}>
                    <TabsList customVariant="full">
                        <Tab key={TABS.SECTOR} label={t("portfolio-distribution.tabs.by-sector")} />
                        <Tab key={TABS.COUNTRY} label={t("portfolio-distribution.tabs.by-country")} />
                    </TabsList>
                    <TabPanel value={TABS.SECTOR} sx={TAB_PANEL_STYLES}>
                        <ErrorBoundary>{PieChart}</ErrorBoundary>
                    </TabPanel>
                    <TabPanel value={TABS.COUNTRY} sx={TAB_PANEL_STYLES}>
                        <ErrorBoundary>{MapChart}</ErrorBoundary>
                    </TabPanel>
                </TabsContext>
            </Box>
        </>
    );
};
